﻿/**
 * Canonical action catalog for employee module permissions.
 * Mirrors SQL `assert_module_permission` mapped flags + `actions` jsonb keys.
 *
 * Actions convention (TS + SQL):
 * - Mapped flags: view, edit/operate/sell/open/close/create, void, discount,
 *   manage_agenda, view_others â†’ boolean columns on employee_branch_permissions.
 * - Other keys: stored in `actions` jsonb; allowed when value is JSON true or string "true".
 * - Restrictive keys: never inherit canView/canEdit when absent (fail-closed; owner/explicit only).
 */

import type { ModuleKey } from '../store/useSessionStore';

/** Actions resolved via dedicated EBP columns (not actions jsonb). */
export const MAPPED_PERMISSION_ACTIONS = [
  'view',
  'edit',
  'operate',
  'sell',
  'open',
  'close',
  'create',
  'void',
  'discount',
  'manage_agenda',
  'view_others',
] as const;

export type MappedPermissionAction = (typeof MAPPED_PERMISSION_ACTIONS)[number];

/**
 * High-risk actions: require explicit `actions[key] === true` (or owner).
 * Do not inherit from canView/canEdit when the key is absent.
 */
export const RESTRICTIVE_PERMISSION_ACTIONS = [
  'edit_pos_rules',
  'edit_fiscal',
  'manage_mp',
  'manage_fiscal',
  'manage_other',
  'manage_integrations',
  'manage_branches',
  'change_type',
  'close_business',
  'mutate',
  'view_branch_tickets',
  'invite',
  'assign_branches',
  'edit_permissions',
  'revoke',
  'return',
] as const;

export type RestrictivePermissionAction = (typeof RESTRICTIVE_PERMISSION_ACTIONS)[number];

/** Per-module action keys from the product matrix (foundation catalog). */
export const MODULE_ACTION_CATALOG: Record<ModuleKey, readonly string[]> = {
  POS: [
    'view',
    'sell',
    'sales_view',
    'discount',
    'price_override',
    'sell_on_credit',
    'void',
    'reprint',
    'view_cost',
    'export',
    'external_services',
  ],
  Orders: [
    'view',
    'accept',
    'reject',
    'mark_ready',
    'mark_delivered',
    'cancel',
    'refund',
    'view_contact',
  ],
  Tables: ['view', 'operate', 'reopen', 'manage_layout'],
  Kitchen: ['view', 'update_status', 'view_prices', 'view_customer', 'manage_stations'],
  MenuOptions: ['view', 'edit', 'edit_price', 'publish'],
  Catalog: [
    'view',
    'edit_content',
    'edit_price',
    'edit_cost',
    'publish',
    'import_export',
    'manage_masters',
  ],
  Inventory: [
    'view',
    'receive',
    'adjust',
    'transfer',
    'count',
    'approve_adjust',
    'view_valuation',
    'export',
  ],
  Customers: [
    'view',
    'edit_contact',
    'view_debt',
    'collect',
    'adjust_debt',
    'set_credit_limit',
    'manage_tasks',
    'export',
  ],
  Promotions: ['view', 'edit', 'activate', 'export'],
  PriceLists: ['view', 'edit'],
  Loyalty: ['view', 'edit'],
  Expenses: ['view', 'create', 'approve', 'pay', 'void', 'export', 'manage_categories'],
  Purchases: ['view', 'edit', 'receive', 'void', 'return', 'view_cost', 'export'],
  // Caja 10/10 (P1-07): open/close = aliases de can_edit (UI `canEdit`; server `Cash/edit`).
  // Sin export/view_others — no hay gate UI ni RPC de Caja que los consuma.
  Cash: ['view', 'open', 'close', 'movement', 'void', 'correct'],
  // Cartera de cheques (Fase C): edit = depositar/devolver (operativo de cajero).
  // endorse/reject mueven plata o re-cargan deuda → toggles apagables por empleado.
  Checks: ['view', 'edit', 'endorse', 'reject'],
  Reports: [
    'view',
    'sales',
    'cash',
    'expenses',
    'inventory',
    'fiado',
    'margins',
    'by_employee',
    'purchases',
    'appointments',
    'export',
  ],
  Agenda: [
    'view',
    'edit',
    'cancel',
    'view_others',
    'manage_agenda',
    'manage_resources',
    'manage_hours',
    'manage_time_off',
    'manage_services',
  ],
  WorkOrders: [
    'view',
    'edit',
    'change_status',
    'view_others',
    'assign_tasks',
    'manage_parts',
    'manage_photos',
    'manage_checklists',
    'link_sale',
    'link_appointment',
  ],
  Commissions: [
    'view',
    'edit',
    'void',
    'view_others',
    'manage_plans',
    'manage_adjustments',
    'settle',
    'approve',
    'pay',
  ],
  BranchSettings: [
    'view',
    'edit',
    'edit_profile',
    'edit_hours',
    'publish',
    'edit_zones',
    'toggle',
    'reply',
    'hide',
    'export',
    'view_sensitive',
    'download',
    'send',
    'convert',
    'view_cost',
    'edit_commercial',
    'edit_fiscal',
    'edit_pos_rules',
    'manage_integrations',
    'manage_mp',
    'manage_fiscal',
    'manage_other',
  ],
  Team: [
    'view',
    'invite',
    'assign_branches',
    'edit_permissions',
    'revoke',
    'history',
    'export',
  ],
  Organization: ['view', 'edit', 'manage_branches', 'change_type', 'close_business'],
  Billing: ['view', 'mutate'],
  Support: ['view', 'view_branch_tickets'],
};

export type PermissionAction = string;

export function isMappedPermissionAction(action: string): action is MappedPermissionAction {
  return (MAPPED_PERMISSION_ACTIONS as readonly string[]).includes(action);
}

export function isRestrictivePermissionAction(action: string): boolean {
  return (RESTRICTIVE_PERMISSION_ACTIONS as readonly string[]).includes(action);
}

/** Spanish labels for Personalizar toggles (jsonb actions + a few mapped ones shown as switches). */
export const ACTION_LABEL_ES: Record<string, string> = {
  view: 'Ver',
  edit: 'Editar',
  operate: 'Operar',
  sell: 'Cobrar / vender',
  open: 'Abrir',
  close: 'Cerrar',
  create: 'Crear',
  void: 'Anular',
  discount: 'Descontar',
  manage_agenda: 'Administrar agenda',
  view_others: 'Ver trabajos de otros',
  price_override: 'Cambiar precio',
  sell_on_credit: 'Vender a cuenta',
  refund: 'Devolver',
  return: 'Devolver a proveedor',
  reprint: 'Reimprimir ticket',
  view_cost: 'Ver costo',
  external_services: 'Recargas y servicios',
  accept: 'Aceptar',
  reject: 'Rechazar',
  mark_ready: 'Marcar listo',
  mark_delivered: 'Marcar entregado',
  cancel: 'Cancelar',
  view_contact: 'Ver contacto del cliente',
  reopen: 'Reabrir',
  manage_layout: 'Configurar salón',
  update_status: 'Actualizar estados',
  view_prices: 'Ver precios',
  view_customer: 'Ver cliente',
  manage_stations: 'Configurar estaciones',
  edit_price: 'Cambiar precio',
  publish: 'Publicar',
  edit_content: 'Editar contenido',
  edit_cost: 'Editar costo',
  import_export: 'Importar / exportar',
  manage_masters: 'Gestionar maestros',
  receive: 'Recibir mercadería',
  adjust: 'Ajustar stock',
  transfer: 'Transferir',
  count: 'Contar / inventario',
  approve_adjust: 'Aprobar ajustes',
  view_valuation: 'Ver valuación',
  export: 'Exportar',
  edit_contact: 'Editar contacto',
  view_debt: 'Ver deuda',
  collect: 'Cobrar',
  adjust_debt: 'Ajustar deuda',
  set_credit_limit: 'Definir límite de crédito',
  activate: 'Activar',
  approve: 'Aprobar',
  pay: 'Pagar',
  manage_categories: 'Gestionar categorías',
  movement: 'Registrar movimiento',
  correct: 'Corregir diferencia',
  endorse: 'Endosar a proveedor',
  sales: 'Informes de ventas',
  cash: 'Informes de caja',
  expenses: 'Informes de gastos',
  inventory: 'Informes de stock',
  customer: 'Informes de cuentas',
  margins: 'Márgenes',
  by_employee: 'Por empleado',
  purchases: 'Compras',
  appointments: 'Informes de agenda',
  manage_resources: 'Gestionar recursos',
  manage_hours: 'Gestionar horarios',
  manage_time_off: 'Gestionar ausencias',
  manage_services: 'Gestionar servicios',
  edit_profile: 'Editar perfil de tienda',
  edit_hours: 'Editar horarios de tienda',
  edit_zones: 'Editar zonas de delivery',
  toggle: 'Activar / pausar delivery',
  reply: 'Responder',
  hide: 'Ocultar reseña',
  view_sensitive: 'Ver datos sensibles',
  download: 'Descargar adjuntos',
  send: 'Enviar cotización',
  convert: 'Convertir / aceptar cotización',
  edit_commercial: 'Editar identidad comercial',
  edit_fiscal: 'Editar datos fiscales',
  edit_pos_rules: 'Editar reglas de venta',
  manage_integrations: 'Gestionar integraciones',
  manage_mp: 'Gestionar Mercado Pago',
  manage_fiscal: 'Gestionar facturación electrónica',
  manage_other: 'Gestionar otras integraciones',
  invite: 'Invitar',
  assign_branches: 'Asignar sucursales',
  edit_permissions: 'Editar permisos',
  revoke: 'Revocar acceso',
  history: 'Ver historial',
  manage_branches: 'Administrar sucursales',
  change_type: 'Cambiar rubro',
  close_business: 'Cerrar negocio',
  mutate: 'Cambiar / pagar plan',
  view_branch_tickets: 'Ver tickets de la sucursal',
};

export function actionLabelEs(action: string): string {
  return ACTION_LABEL_ES[action] ?? action.replace(/_/g, ' ');
}

/** Actions stored in `actions` jsonb (exclude mapped column flags). */
export function customizableActionsForModule(module: ModuleKey): readonly string[] {
  return MODULE_ACTION_CATALOG[module].filter((a) => !isMappedPermissionAction(a));
}